import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { MapPin, ArrowLeft, Mail } from 'lucide-react';
import { motion } from 'framer-motion';

const ForgotPasswordPage = () => {
  const { forgotPassword, error } = useAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLocalError('');
    
    const result = await forgotPassword(email);
    
    if (result.success) {
      setSuccess(true);
    } else {
      setLocalError(result.error);
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Card className="border-[#E4E4E7] shadow-sm">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-[#0055FF] rounded-xl flex items-center justify-center">
                <MapPin className="w-8 h-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-[#09090B]">Password Dimenticata</CardTitle>
            <CardDescription className="text-[#71717A]">
              Inserisci la tua email per ricevere un link di reset
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {success ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <Mail className="w-8 h-8 text-[#34C759]" />
                </div>
                <p className="text-[#09090B]">
                  Se l'email esiste, riceverai un link per reimpostare la password.
                </p>
                <Link to="/login">
                  <Button 
                    className="w-full bg-[#0055FF] hover:bg-[#0040D0] text-white"
                    data-testid="back-to-login-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Torna al Login
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                {(localError || error) && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                    {localError || error}
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-semibold text-[#09090B]">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="nome@esempio.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="forgot-email-input"
                      className="border-[#E4E4E7] focus:ring-[#0055FF] focus:border-[#0055FF]"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={isLoading}
                    data-testid="forgot-submit-btn"
                    className="w-full bg-[#0055FF] hover:bg-[#0040D0] text-white font-semibold py-3"
                  >
                    {isLoading ? 'Invio in corso...' : 'Invia Link di Reset'}
                  </Button>
                </form>
                
                <div className="text-center">
                  <Link 
                    to="/login" 
                    className="text-sm text-[#0055FF] hover:underline inline-flex items-center gap-1"
                    data-testid="back-link"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Torna al Login
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
